/* eslint-disable */
// Detección y metadata de los sistemas externos de interconexión que
// alimentan la red de reportantes / incidentes. El objetivo es que el
// operador identifique de un vistazo si un reportante o un incidente
// recibe positions de un proveedor 9-1-1 / SOS específico, sin tener
// que abrir el drawer.
//
// Proveedores conocidos (orden de prioridad de detección):
//   carbyne  — plataforma 9-1-1 oficial integrada por Carbyne. Llega
//              como `additionalData.systemName === 'CARBYNE'`.
//   didi     — llamadas SOS desde la app DIDI conductor/usuario. Llega
//              como `additionalData.systemName === 'DIDI'` (con
//              `didiInfo` adjunto).
//   uber     — llamadas SOS desde la app Uber. Anidado dentro de un
//              payload RAPID-SOS como `additionalData.uber` (no es un
//              systemName independiente).
//   rapidsos — RapidSOS network (default cuando hay positions del
//              reportante pero ninguna de las otras pistas). Cubre
//              Apple Emergency Contact, Android, OnStar lite, etc.
//
// Activos visuales: 4 PNGs en `public/` (carbyne_small.png, didi.png,
// uber.png, rapidsos.png). NO se recolorean por tema porque son logos
// de marca; se mantienen fijos.

var PARTNERS = {
  carbyne: {
    id: 'carbyne',
    label: 'Carbyne',
    logo: '/carbyne_small.png',
    logoLarge: '/carbyne.png',
  },
  didi: {
    id: 'didi',
    label: 'DiDi',
    logo: '/didi.png',
    logoLarge: '/didi.png',
  },
  uber: {
    id: 'uber',
    label: 'Uber',
    logo: '/uber.png',
    logoLarge: '/uber.png',
  },
  rapidsos: {
    id: 'rapidsos',
    label: 'RapidSOS',
    logo: '/rapidsos.png',
    logoLarge: '/rapidsos.png',
  },
}

// Detecta el proveedor a partir de un payload heterogéneo. Acepta el
// shape de un reportante (con `additionalData`), un incidente
// enriquecido (con `rapidSosLocations` + `systemName` adjunto) o un
// resultObject directo del HTTP `/rapidsos`.
//
// Devuelve el ID string del proveedor (`'carbyne' | 'didi' | 'uber' |
// 'rapidsos'`) o null si el payload no aporta señales.
//
// `entityType` (opcional): cuando se sabe que el payload representa
// un reportante (entityType='reportant'), defaultea a 'rapidsos' como
// proveedor base — TODO reportante de la red llega por la
// infraestructura de RapidSOS, aunque el specific systemName venga
// vacío o sin señales explícitas. Esto cubre el caso del WS legacy
// que no siempre setea additionalData.systemName.
//
// Replica la jerarquía del legacy MapAlerts.getPartnerLogo (Carbyne >
// DiDi > Uber > RapidSOS default).
export function detectPartnerSystem(payload, entityType) {
  if (!payload) return null

  // El detector se usa con shapes heterogéneos:
  //   - El RAW del backend → tiene additionalData / rapidSosLocations / positions directo
  //   - El alert wrapper de MapV2.vue → guarda el raw bajo `data.raw`
  //   - El monitor.data del normalizer → es el raw original
  //   - El payload del trip socket → directo
  // Probamos las rutas comunes en orden, devolviendo el primer hit.
  var candidates = [
    payload,
    payload.raw,                               // alert wrapper
    payload.data,                              // monitor / componente Vue (this)
    payload.data && payload.data.raw,          // alert wrapper anidado
  ].filter(Boolean)

  for (var ci = 0; ci < candidates.length; ci++) {
    var c = candidates[ci]
    var add = c.additionalData || null
    var sys = (add && add.systemName) || c.systemName || null

    // 1. Carbyne / DiDi por systemName explícito (hereda al primer hit).
    if (sys === 'CARBYNE') return 'carbyne'
    if (sys === 'DIDI') return 'didi'

    // 2. Uber viaja anidado bajo un payload RAPID-SOS.
    if (add && add.uber) return 'uber'
    if (c.uber) return 'uber'

    // 3. RapidSOS por systemName explícito.
    if (sys === 'RAPID-SOS') return 'rapidsos'
  }

  // 4. Señales de positions para inferir RapidSOS network. Recorremos
  //    todos los candidatos buscando rapidSosLocations / positions.
  for (var pi = 0; pi < candidates.length; pi++) {
    var p = candidates[pi]
    if (Array.isArray(p.rapidSosLocations) && p.rapidSosLocations.length > 0) return 'rapidsos'
    if (Array.isArray(p.positions) && p.positions.length > 0) return 'rapidsos'
  }

  // 5. Default por entityType: un reportante SIEMPRE viene del
  //    ecosistema RapidSOS aunque no haya señales explícitas. Sin este
  //    fallback, reportantes con additionalData incompleto quedaban
  //    sin badge de fuente, dando la falsa impresión de que no tienen
  //    interconexión externa.
  if (entityType === 'reportant') return 'rapidsos'

  return null
}

// Resuelve la metadata completa del proveedor (label + logo) por id.
// Retorna null si el id no es conocido — el caller decide ocultar UI.
export function getPartnerMeta(id) {
  if (!id) return null
  return PARTNERS[id] || null
}

// Listado de IDs conocidos — útil para precargar sprites del badge en
// el icon-registry o iterar al inicializar UI.
export function listPartnerIds() {
  return Object.keys(PARTNERS)
}
